/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CMLOrHTML } from 'bundles/cml';
import FormPart from 'bundles/compound-assessments/components/form-parts/FormPart';
import getFormPartData from 'bundles/compound-assessments/components/form-parts/lib/getFormPartData';
import { GradeFeedbackStrings } from 'bundles/compound-assessments/lib/utils/getQuestionGradeFeedbackInfo';
import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
import type { InVideoQuestion, VideoQuizState } from 'bundles/video-quiz/types';
import transformQuestionToFormData from 'bundles/video-quiz/utils/transformQuestionToFormData';

type Props<T extends QuizQuestionPrompt> = {
  question: InVideoQuestion<T> | null;
  isReadonly?: boolean;
  currentState: VideoQuizState;
};

const styles = {
  questionArea: css`
    overflow-y: scroll;
    padding: 1.5rem;

    &::-webkit-scrollbar {
      -webkit-appearance: none;
    }

    &::-webkit-scrollbar:vertical {
      width: 11px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 8px;
      border: 2px solid var(--cds-color-neutral-background-primary);
      background-color: rgb(0 0 0 / 50%);
    }

    &::-webkit-scrollbar-track {
      background-color: var(--cds-color-neutral-background-primary);
      border-radius: 8px;
    }
  `,
  responseBox: css`
    margin-top: 1.25rem;
  `,
  legend: css`
    margin-bottom: 0;
    border-bottom: none;
    display: contents;
  `,
};

class VideoQuizQuestionArea<T extends QuizQuestionPrompt> extends React.Component<Props<T>> {
  private declare containerRef: HTMLFieldSetElement | null;

  private declare responseBoxRef: HTMLDivElement | null;

  constructor(props: Props<T> | Readonly<Props<T>>) {
    super(props);
    this.containerRef = null;
    this.responseBoxRef = null;
  }

  componentDidUpdate() {
    // Attempt to show the failure feedback first
    let selector = `[data-test='GradeFeedback-${GradeFeedbackStrings.FAILURE}]`;

    // If there isn't any, just show the first piece of feedback
    if (!this.containerRef?.querySelector(selector)) {
      selector = "[data-test^='GradeFeedback-']";
    }

    // Focus on first checkbox after retry has been triggered
    if (this.props.currentState === 'ready') {
      const groupDiv = this.responseBoxRef?.querySelector('[role="group"]');

      if (groupDiv) {
        const checkboxes = groupDiv.querySelectorAll('input[type="checkbox"]');
        if (checkboxes.length) {
          const firstCheckbox = checkboxes[0] as HTMLInputElement;
          firstCheckbox.focus();
        }
      }
    }

    const element = this.containerRef?.querySelector(selector) as HTMLElement | null;

    if (element) {
      if (this.containerRef) {
        this.containerRef.scrollTop = element.offsetTop;
      }
    }
  }

  render() {
    const { question, isReadonly, currentState } = this.props;

    const isDisabled = currentState === 'done' || currentState === 'retry';

    if (!question) {
      return null;
    }

    const {
      root: { element, children },
    } = transformQuestionToFormData(question);

    const { promptText } = getFormPartData(element);

    return (
      <fieldset
        css={styles.questionArea}
        ref={(r) => {
          this.containerRef = r;
        }}
        className="rc-VideoQuizQuestionArea"
        // tabIndex needed so users can scroll through grade feedback with the keyboard
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        <legend className="rc-VideoQuizQuestionArea__legend" css={styles.legend}>
          <CMLOrHTML value={promptText} />
        </legend>
        <div
          className="rc-VideoQuizQuestionArea__responseBox"
          css={styles.responseBox}
          ref={(r) => {
            this.responseBoxRef = r;
          }}
          aria-live="polite"
        >
          <FormPart isReadOnly={isReadonly} isDisabled={isDisabled} formPart={element} enableFeedbackFocus />
          {children && children.length > 0 && (
            <div>
              {children.map(({ element: childFormPart }) => (
                <FormPart isReadOnly={isReadonly} isDisabled={isDisabled} formPart={childFormPart} />
              ))}
            </div>
          )}
        </div>
      </fieldset>
    );
  }
}

export default VideoQuizQuestionArea;
